import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCurrency } from '../../context/CurrencyContext';
import API from '../../utils/api';
import { getFulfillment } from '../../utils/fulfillment';

const pad = n => String(n).padStart(2, '0');

function Countdown({ initial }) {
  const [time, setTime] = useState(initial || [0,0,0]);
  useEffect(() => {
    if (!initial) return;
    const id = setInterval(() => {
      setTime(([h,m,s]) => {
        s--; if(s<0){s=59;m--} if(m<0){m=59;h--} if(h<0) return [0,0,0];
        return [h,m,s];
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const [h,m,s] = time;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:4, color:'#C0392B', fontSize:11, fontWeight:700 }}>
      ⏱ <span className="countdown-pip">{pad(h)}</span>h <span className="countdown-pip">{pad(m)}</span>m <span className="countdown-pip">{pad(s)}</span>s
    </div>
  );
}

export default function ListingCard({ listing, delay = 0, onQuickView }) {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const { formatPrice } = useCurrency();
  const [saved, setSaved] = useState(listing.isSaved || false);
  const [saving, setSaving] = useState(false);
  const [hoursRemaining, setHoursRemaining] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const { id, slug, title, listing_type, price, current_bid, starting_bid, unit, primary_image, category_label, category_icon, rating_avg, rating_count, dist, location_city, location_state, badgeColor, countdown, promotion, free_shipping, save_count, quantity, flash_sale_discount, flash_sale_ends_at, flash_sale_original_price, condition, expires_at, id_verified, accepts_offers, bundle_discount, published_at, price_was } = listing;
  const listingPath = listing_type === 'barter' ? `/barter/${slug || id}` : `/listing/${slug || id}`;
  const displayPrice = listing_type === 'auction' ? (current_bid || starting_bid || 0) : price;
  const isFlashSale = flash_sale_discount && flash_sale_ends_at && new Date(flash_sale_ends_at) > new Date();
  const isPriceReduced = !isFlashSale && price_was && parseFloat(price_was) > parseFloat(price || 0) * 1.05 && listing_type === 'sale';
  const expiryDaysLeft = expires_at ? Math.ceil((new Date(expires_at) - new Date()) / 86400000) : null;
  const showExpiryBadge = expiryDaysLeft !== null && expiryDaysLeft >= 0 && expiryDaysLeft <= 5;
  const isJustListed = published_at && (Date.now() - new Date(published_at)) < 24 * 3600 * 1000;

  const badge = listing_type === 'rent' ? 'For Rent' : listing_type === 'auction' ? 'Live Bid' : listing_type === 'barter' ? 'Barter' : listing_type === 'free' ? 'Free' : 'For Sale';
  const color = listing_type === 'rent' ? '#1A5276' : listing_type === 'auction' ? '#C0392B' : listing_type === 'barter' ? '#7B2D8B' : listing_type === 'free' ? '#0E7490' : '#2D7A3A';
  // UX r2: how the item reaches the buyer, from the same helper as the listing page (pickup only,
  // ships, or both). Not shown for rentals, barter or digital items, which have their own flows.
  const fulfillment = ['rent', 'barter', 'digital'].includes(listing_type) ? null : getFulfillment(listing, formatPrice);
  const fulfillmentPill = !fulfillment ? null
    : fulfillment.pickupOnly ? { text: '📍 Local pickup only', color: '#1A5276', bg: '#EBF0FF', border: '#C9D6F2' }
    : fulfillment.both ? { text: fulfillment.freeShipping ? '🚚 Free shipping or 📍 pickup' : '🚚 Ships or 📍 local pickup', color: '#2D7A3A', bg: '#EAF5EC', border: '#C8E6C9' }
    : fulfillment.shipOnly ? { text: fulfillment.freeShipping ? '🚚 Free shipping' : '🚚 Ships', color: '#2D7A3A', bg: '#EAF5EC', border: '#C8E6C9' }
    : null;
  const actionLabel = listing_type === 'rent' ? 'Book Now' : listing_type === 'auction' ? 'Bid' : listing_type === 'barter' ? 'Trade' : listing_type === 'free' ? 'Claim' : 'Buy';

  useEffect(() => {
    if (!promotion || !promotion.expires_at) return;
    const updateCountdown = () => {
      const now = new Date().getTime();
      const expires = new Date(promotion.expires_at).getTime();
      const diff = Math.max(0, expires - now);
      const hours = Math.floor(diff / (1000 * 60 * 60));
      setHoursRemaining(hours);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [promotion]);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user) { navigate('/login'); return; }
    setSaved(prev => !prev);
    try {
      const { data } = await API.post(`/listings/${id}/save`);
      setSaved(data.saved);
    } catch {
      setSaved(prev => !prev);
    }
  };

  return (
    <div
      className="listing-card fade-up"
      style={{ animationDelay: `${delay}s` }}
    >
      <a href={listingPath} className="lcard-link" aria-label={title} />
      <div className="lcard-img">
        <div className="lcard-img-inner" style={{ background: (primary_image && !imgFailed) ? undefined : 'var(--cream)' }}>
          {primary_image && !imgFailed
            ? <img src={primary_image.startsWith('http') ? primary_image : `${process.env.REACT_APP_API_URL?.replace('/api','')}${primary_image}`} alt={title} loading="lazy" className={imgLoaded ? 'img-loaded' : ''} onLoad={() => setImgLoaded(true)} onError={() => setImgFailed(true)} />
            : <span style={{ fontSize: 52, opacity: .7 }}>{category_icon || '📦'}</span>
          }
        </div>
        {isFlashSale && (
          <div style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'linear-gradient(135deg, #C0392B 0%, #E74C3C 100%)',
            color: 'white',
            padding: '5px 10px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
          }}>
            🔥 {Math.round(flash_sale_discount)}% OFF
          </div>
        )}
        {showExpiryBadge && !isFlashSale && (
          <div style={{ position:'absolute', top:8, right:8, background:'rgba(192,57,43,0.9)', color:'white', padding:'4px 9px', borderRadius:20, fontSize:10, fontWeight:700, boxShadow:'0 2px 6px rgba(0,0,0,0.2)' }}>
            ⏰ {expiryDaysLeft === 0 ? 'Last day' : `${expiryDaysLeft}d left`}
          </div>
        )}
        {!isFlashSale && promotion && hoursRemaining > 0 && (
          <div style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#1C1C1C',
            padding: '6px 12px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            ⭐ Promoted {hoursRemaining}h
          </div>
        )}
        {isJustListed && !isFlashSale && !promotion && (
          <div style={{ position:'absolute', top:8, left:8, background:'linear-gradient(135deg,#2D7A3A 0%,#1A8044 100%)', color:'white', padding:'4px 10px', borderRadius:20, fontSize:10, fontWeight:700, boxShadow:'0 2px 6px rgba(0,0,0,0.18)', letterSpacing:.2 }}>
            ✨ Just Listed
          </div>
        )}
        {isPriceReduced && !isJustListed && !promotion && (
          <div style={{ position:'absolute', top:8, left:8, background:'linear-gradient(135deg,#1565C0 0%,#1976D2 100%)', color:'white', padding:'4px 10px', borderRadius:20, fontSize:10, fontWeight:700, boxShadow:'0 2px 6px rgba(0,0,0,0.22)', letterSpacing:.2 }}>
            ↓ Price Drop
          </div>
        )}
        <div className="lcard-badge" style={{ background: color }}>{badge}</div>
        {listing_type === 'sale' && parseInt(quantity) > 0 && parseInt(quantity) <= 5 && (
          <div style={{ position:'absolute', bottom:8, left:8, background: parseInt(quantity) === 1 ? 'rgba(176,0,32,0.92)' : 'rgba(184,116,20,0.92)', color:'white', padding:'4px 9px', borderRadius:20, fontSize:10, fontWeight:700, boxShadow:'0 2px 6px rgba(0,0,0,0.25)', pointerEvents:'none' }}>
            {parseInt(quantity) === 1 ? '🔥 Last one!' : `Only ${quantity} left`}
          </div>
        )}
        <button className="lcard-fav" onClick={handleSave} disabled={saving}>
          {saved ? '❤️' : '🤍'}
        </button>
        {onQuickView && (
          <button className="lcard-qv-btn" onClick={e => { e.stopPropagation(); e.preventDefault(); onQuickView(listing); }}>
            👁 Quick View
          </button>
        )}
      </div>

      <div className="lcard-body">
        <div className="lcard-cat">
          {category_icon} {category_label}
          {free_shipping && !fulfillmentPill && <span style={{ marginLeft:6, fontSize:10, fontWeight:700, color:'#2D7A3A', background:'#EAF5EC', border:'1px solid #C8E6C9', padding:'1px 7px', borderRadius:100 }}>🚚 Free Ship</span>}
          {condition && condition !== 'used' && (() => {
            const cmap = {
              new:        { label:'✨ New',      color:'#0E7490', bg:'#E0F7FA', border:'#B2EBF2' },
              like_new:   { label:'💎 Like New', color:'#2D7A3A', bg:'#EAF5EC', border:'#C8E6C9' },
              excellent:  { label:'⭐ Excellent', color:'#2D7A3A', bg:'#EAF5EC', border:'#C8E6C9' },
              very_good:  { label:'👍 Very Good', color:'#C9930A', bg:'#FFF9E6', border:'#FCE8A6' },
              good:       { label:'👍 Good',      color:'#C9930A', bg:'#FFF9E6', border:'#FCE8A6' },
              fair:       { label:'Fair',         color:'var(--text-soft)', bg:'var(--bg)', border:'var(--border)' },
            };
            const c = cmap[condition];
            if (!c) return null;
            return (
              <span style={{ marginLeft:6, fontSize:10, fontWeight:700, borderRadius:100, padding:'1px 7px', color:c.color, background:c.bg, border:`1px solid ${c.border}` }}>
                {c.label}
              </span>
            );
          })()}
        </div>
        <div className="lcard-title">{title}</div>
        {fulfillmentPill && (
          <div data-testid="lcard-fulfillment" style={{ marginBottom:6 }}>
            <span style={{ display:'inline-block', fontSize:10, fontWeight:700, borderRadius:100, padding:'2px 8px', color:fulfillmentPill.color, background:fulfillmentPill.bg, border:`1px solid ${fulfillmentPill.border}` }}>
              {fulfillmentPill.text}
            </span>
          </div>
        )}

        {listing_type === 'auction' && countdown && <Countdown initial={countdown} />}

        <div className="lcard-meta">
          {rating_count > 0 ? (
            <>
              <span style={{ color: '#C9930A' }}>★</span>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{parseFloat(rating_avg || 0).toFixed(1)}</span>
              <span>({rating_count})</span>
              <span style={{ color: '#E8E2DC' }}>·</span>
            </>
          ) : (
            // UX r2: was a green "New" chip that read like the item's condition next to "Good".
            <span style={{ fontSize:10, fontWeight:600, color:'var(--text-mid)', marginRight:4 }}>No reviews yet</span>
          )}
          {/* QA r3: no literal "null, null" — only show the parts that exist, and hide the pin when empty. */}
          {(() => {
            const loc = dist || [location_city, location_state].filter(v => v != null && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'null').join(', ');
            return loc ? <span>📍 {loc}</span> : null;
          })()}
          {parseInt(save_count) > 2 && <><span style={{ color:'#E8E2DC' }}>·</span><span style={{ color:'#C0392B', fontSize:10, fontWeight:700 }}>❤️ {save_count} saved</span></>}
          {id_verified && <><span style={{ color:'#E8E2DC' }}>·</span><span style={{ fontSize:10, fontWeight:700, color:'#1A5276' }}>✅ ID Verified</span></>}
          {accepts_offers && listing_type === 'sale' && !isFlashSale && <><span style={{ color:'#E8E2DC' }}>·</span><span style={{ fontSize:10, fontWeight:700, color:'#7B2D8B' }}>💰 Offers OK</span></>}
          {bundle_discount?.is_active && <><span style={{ color:'#E8E2DC' }}>·</span><span style={{ fontSize:10, fontWeight:700, color:'#1A5276' }}>🎁 Bundle deals</span></>}
        </div>

        <div className="lcard-footer">
          <div>
            {listing_type === 'auction' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#C0392B', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 1 }}>Current bid</span>
            )}
            {listing_type === 'free'
              ? <span className="lcard-price" style={{ color:'#0E7490' }}>Free</span>
              : listing_type === 'barter'
              ? <span className="lcard-price" style={{ color:'#7B2D8B', fontSize:14 }}>Trade</span>
              : isFlashSale
              ? <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                  <span className="lcard-price" style={{ color:'#C0392B' }}>{formatPrice(displayPrice || 0)}</span>
                  <span style={{ fontSize:11, color:'var(--text-soft)', textDecoration:'line-through' }}>{formatPrice(flash_sale_original_price || 0)}</span>
                  <span className="lcard-unit">{listing_type === 'rent' ? '/day' : ''}</span>
                </div>
              : isPriceReduced
              ? <div style={{ display:'flex', alignItems:'baseline', gap:5 }}>
                  <span className="lcard-price" style={{ color:'#1565C0' }}>{formatPrice(displayPrice || 0)}</span>
                  <span style={{ fontSize:11, color:'var(--text-soft)', textDecoration:'line-through' }}>{formatPrice(parseFloat(price_was))}</span>
                </div>
              : <><span className="lcard-price">{formatPrice(displayPrice || 0)}</span><span className="lcard-unit">{listing_type === 'rent' ? '/day' : ''}</span></>
            }
          </div>
          <div style={{ display:'flex', gap:6, position:'relative', zIndex:2 }}>
            {accepts_offers && listing_type === 'sale' && !isFlashSale && (
              <button
                className="btn btn-sm btn-outline-cherry"
                style={{ padding:'7px 10px', fontSize:12, fontWeight:700 }}
                onClick={e => { e.stopPropagation(); navigate(`${listingPath}?offer=1`); }}
                title="Make an offer"
              >Offer</button>
            )}
            <button
              className="btn btn-sm"
              style={{ background: color, color: 'white', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700 }}
              onClick={e => { e.stopPropagation(); navigate(listingPath); }}
            >{actionLabel}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
